import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SettingsPanel = ({ settings, onUpdateSettings }) => {
  const [formData, setFormData] = useState({
    emailNotifications: settings?.emailNotifications,
    smsNotifications: settings?.smsNotifications,
    marketingEmails: settings?.marketingEmails,
    locationSharing: settings?.locationSharing,
    profileVisibility: settings?.profileVisibility,
    defaultLocation: settings?.defaultLocation,
    searchRadius: settings?.searchRadius
  });

  const [isUpdating, setIsUpdating] = useState(false);

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setIsUpdating(true);
    await onUpdateSettings(formData);
    setIsUpdating(false);
  };

  const searchRadiusOptions = [
    { value: '5', label: '5 miles' },
    { value: '10', label: '10 miles' },
    { value: '25', label: '25 miles' },
    { value: '50', label: '50 miles' },
    { value: '100', label: '100 miles' },
    { value: 'unlimited', label: 'UK-wide' }
  ];

  return (
    <div className="space-y-6">
      {/* Notification Preferences */}
      <div className="bg-card rounded-lg p-6 shadow-organic">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Bell" size={20} className="mr-2" />
          Notification Preferences
        </h3>
        
        <div className="space-y-4">
          <Checkbox
            label="Email Notifications"
            description="Receive updates about your bookings and saved items"
            checked={formData?.emailNotifications}
            onChange={(e) => handleCheckboxChange('emailNotifications', e?.target?.checked)}
          />
          
          <Checkbox
            label="SMS Notifications"
            description="Get text reminders for upcoming events"
            checked={formData?.smsNotifications}
            onChange={(e) => handleCheckboxChange('smsNotifications', e?.target?.checked)}
          />
          
          <Checkbox
            label="Marketing Emails"
            description="Receive newsletters and promotional content"
            checked={formData?.marketingEmails}
            onChange={(e) => handleCheckboxChange('marketingEmails', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Privacy Settings */}
      <div className="bg-card rounded-lg p-6 shadow-organic">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Shield" size={20} className="mr-2" />
          Privacy Settings
        </h3>
        
        <div className="space-y-4">
          <Checkbox
            label="Location Sharing"
            description="Allow us to use your location for better local recommendations"
            checked={formData?.locationSharing}
            onChange={(e) => handleCheckboxChange('locationSharing', e?.target?.checked)}
          />
          
          <Checkbox
            label="Public Profile"
            description="Make your profile visible to other users"
            checked={formData?.profileVisibility}
            onChange={(e) => handleCheckboxChange('profileVisibility', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Location Preferences */}
      <div className="bg-card rounded-lg p-6 shadow-organic">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="MapPin" size={20} className="mr-2" />
          Location Preferences
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Default Location"
            name="defaultLocation"
            value={formData?.defaultLocation}
            onChange={handleInputChange}
            placeholder="Enter city or postcode"
            description="Used for local-first search results"
          />
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Search Radius
            </label>
            <select
              name="searchRadius"
              value={formData?.searchRadius}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle"
            >
              {searchRadiusOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground mt-1">
              How far to search for local listings
            </p>
          </div>
        </div>
      </div>
      {/* Account Actions */}
      <div className="bg-card rounded-lg p-6 shadow-organic">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Settings" size={20} className="mr-2" />
          Account Actions
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div>
              <h4 className="font-medium text-foreground">Export Data</h4>
              <p className="text-sm text-muted-foreground">
                Download a copy of your account data
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg bg-destructive/5">
            <div>
              <h4 className="font-medium text-foreground">Delete Account</h4>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handleSave}
          loading={isUpdating}
          disabled={isUpdating}
          iconName="Save"
          iconPosition="left"
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsPanel;